import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Fade,
  Paper,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import LoadingImage from '../../components/LoadingImage';

const FrigoDorseIsiYalitimi: React.FC = () => {
  useEffect(() => {
    document.title = "Frigo Dorse Kasalarında Isı Yalıtımı ve Poliüretan İzolasyon - Kale Treyler";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin'de frigo dorse ısı yalıtımı, poliüretan izolasyon uygulamaları ve araç içi yalıtım teknikleri. Dorse kasa içi yalıtım süreçleri hakkında detaylar.");
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "araç içi yalıtım Mersin, frigo dorse izolasyon, poliüretan köpük kaplama, dorse ısı yalıtımı, tır dorsesi kasa tamiri");
    }

    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/blog/frigo-dorse-isi-yalitimi');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  return (
    <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            <Button
              component={RouterLink}
              to="/blog"
              startIcon={<ArrowBack />}
              sx={{ mb: 4, textTransform: 'none', fontWeight: 'bold' }}
            >
              Blog Yazılarına Dön
            </Button>

            <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'grey.200', borderRadius: 4 }}>
              <Typography variant="overline" color="secondary" sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}>
                Araç İçi Yalıtım
              </Typography>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, mt: 1, mb: 4, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.2 }}>
                Frigo Dorse Kasalarında Isı Yalıtımı ve Poliüretan İzolasyon Teknikleri
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Yayınlanma Tarihi: <strong>15 Temmuz 2026</strong>
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Yazar: <strong>Kale Treyler Editör</strong>
                </Typography>
              </Box>

              <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                <LoadingImage
                  src="/25.png"
                  alt="Frigo Dorse Isı Yalıtımı ve Poliüretan Kaplama Uygulaması"
                  style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover' }}
                />
              </Box>

              <Box sx={{ typography: 'body1', color: 'text.primary', lineHeight: 1.8 }}>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Uluslararası soğuk zincir taşımacılığında en kritik faktör, dorsenin iç sıcaklığının dış hava şartlarından etkilenmeden sabit kalabilmesidir. Bu noktada devreye giren <strong>frigo dorse izolasyon</strong> uygulamaları, taşınan gıda, ilaç ve kimyasal maddelerin bozulmadan hedefe ulaşması için en önemli unsurdur. Mersin Akdeniz bölgesinde faaliyet gösteren firmamız <RouterLink to="/" style={{ color: '#1e3a8a', fontWeight: 'bold', textDecoration: 'underline' }}>Kale Treyler</RouterLink> bünyesinde, frigo dorseler için yüksek standartlarda yalıtım çözümleri geliştirilmektedir.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Poliüretan Köpük Kaplamanın Önemi
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Dorse kasa yalıtımında en çok tercih edilen yöntemlerin başında poliüretan köpük kaplama gelmektedir. Poliüretan, çok düşük ısı iletkenlik katsayısına sahip olması nedeniyle soğuk havayı içeride, sıcak havayı ise dışarıda tutmada yüksek performans gösterir. Bu yalıtım işlemi dorse panellerinin içine veya taban/tavan bölgelerine profesyonelce enjekte edilir. Doğru uygulanan <strong>dorse ısı yalıtımı</strong>, tırın soğutucu motorunun (frigo motoru) çalışma süresini azaltarak yakıt tasarrufu elde edilmesine katkı sağlar.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Mersin'de Araç İçi Yalıtım Uygulama Aşamaları
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Frigo kasalarda zamanla meydana gelen yıpranmalar, darbe alan paneller veya eskiyen kapı contaları ısı sızıntılarına (ısı köprülerine) yol açar. Mersin MIG tır parkı bölgesindeki servisimizde gerçekleştirdiğimiz <RouterLink to="/hizmetler/arac-ici-yalitim" style={{ color: '#1e3a8a', fontWeight: 'bold', textDecoration: 'underline' }}>araç içi yalıtım</RouterLink> işlemlerinde şu adımları takip ediyoruz:
                </Typography>
                <ul>
                  <li>Termal kameralarla kasadaki ısı kaçaklarının ve zayıf noktaların tespit edilmesi.</li>
                  <li>Eski yalıtım malzemelerinin korozyona yol açmayacak şekilde sökülmesi ve panel içlerinin temizlenmesi.</li>
                  <li>Yüksek yoğunluklu poliüretan izolasyon malzemelerinin özel makineler yardımıyla enjekte edilmesi.</li>
                  <li>Kasa köşeleri ve taban birleşim yerlerinin sızdırmazlık macunlarıyla korumaya alınması.</li>
                </ul>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Enerji Tasarrufu ve Verimlilik ilişkisi
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                  Uluslararası gıda taşımacılığı yapan nakliye firmaları için işletme giderlerinin azaltılması birincil önceliktir. Yalıtımı zayıf bir dorse kasasında, soğutucu ünite sürekli tam güçte çalışmak zorunda kalır. Bu durum hem frigo motorunun ömrünü kısaltır hem de yüksek dizel yakıt tüketimine neden olur. Mersin ve çevre illerdeki tır filolarına sunduğumuz profesyonel <strong>araç içi yalıtım Mersin</strong> hizmetleriyle, dorselerinizde maksimum enerji verimliliği elde edilmesine destek oluyoruz.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 1, bgcolor: 'grey.100' }}>
                  Not: Sitemizde yer alan tüm blog içerikleri lojistik sektörü çalışanları için genel bilgilendirme amacıyla hazırlanmıştır. Araç modelinize ve kullanım alanınıza özel teknik çözümler için lütfen servisimizle iletişime geçiniz.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default FrigoDorseIsiYalitimi;
